import {Form, UI} from "components";
import Moment from "moment";
import {useState, useMemo} from "react";
import {Button, ButtonGroup, Table, Box, Text, VStack} from "@chakra-ui/react";
import useReportBestSelling from "hooks/useReportBestSelling";
import useExportReportType from "hooks/useExportReportType";

export default function AnalyticsBestSelling() {
    const initialPeriod = [
        Moment().startOf("month").toDate(),
        Moment().endOf("month").toDate()
    ];

    const [period, setPeriod] = useState(initialPeriod)
    const [sortBy, setSortBy] = useState(null)
    const [sortOrder, setSortOrder] = useState('desc')

    const validPeriod =
        period && period[0] instanceof Date && period[1] instanceof Date
            ? [Moment(period[0]).format('YYYY-MM-DD'), Moment(period[1]).format('YYYY-MM-DD')]
            : null

    const startDate = validPeriod ? validPeriod[0] : null
    const endDate = validPeriod ? validPeriod[1] : null

    const report = useReportBestSelling(startDate, endDate)
    const exportReport = useExportReportType()

    const productsDataRaw = report.data?.months
        ? Object.entries(report.data.months).flatMap(([month, products]) =>
            products.map(product => ({
                month,
                product: product.product,
                total: parseInt(product.total),
                orders: parseInt(product.orders),
            }))
        )
        : report.data && Array.isArray(report.data)
            ? report.data.map(product => ({
                month: Moment(validPeriod?.[0]).format('MM/YYYY'),
                product: product.product,
                total: parseInt(product.total),
                orders: parseInt(product.orders),
            }))
            : []

    const productsData = useMemo(() => {
        if (!sortBy) return productsDataRaw

        return [...productsDataRaw].sort((a, b) => {
            let aValue = a[sortBy]
            let bValue = b[sortBy]

            if (sortOrder === 'asc') {
                return aValue - bValue
            } else {
                return bValue - aValue
            }
        })
    }, [productsDataRaw, sortBy, sortOrder])

    const columns = [
        {children: 'Mês'},
        {children: 'Produto'},
        {children: 'Qtd. Pedidos', textAlign: 'right'},
        {children: 'Total Vendido', textAlign: 'right'},
    ]

    const handleResetFilters = () => {
        setPeriod(initialPeriod)
        setSortBy(null)
        setSortOrder('desc')
    }

    const handleSortChange = (value) => {
        if (sortBy === value) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
        } else {
            setSortBy(value)
            setSortOrder('desc')
        }
    }

    const handleExportReportType = (reportType, exportMine) => {
        if (startDate && endDate) {
            exportReport.mutate({
                reportType,
                exportMine,
                startDate,
                endDate,
            })
        }
    }

    const handleExportCsv = () => {
        if (!productsData.length) return;

        const header = ["Mês", "Produto", "Qtd. Pedidos", "Total Vendido (unidades)"];

        const csvContent = [
            header.join(";"),
            ...productsData.map((item) => [
                item.month,
                item.product || "",
                item.orders,
                item.total
            ].join(";"))
        ].join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], {type: "text/csv;charset=utf-8;"});
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `relatorio-produtos-mais-vendidos-${Moment(startDate).format('DD-MM-YYYY')}-a-${Moment(endDate).format('DD-MM-YYYY')}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const sortOptions = [
        {value: 'orders', label: 'Pedidos'},
        {value: 'total', label: 'Total'},
    ]

    const hasNoData = !report.isFetching && productsData.length === 0;

    return (
        <>
            <UI.Header title="Produtos Mais Vendidos"/>

            <UI.Table
                loading={report.isFetching}
                loadingText="Buscando produtos mais vendidos..."
                label="Ranking de produtos mais vendidos:"
                columns={columns}
                data={productsData}
                Row={Row}
            >
                <UI.RadioCardGroup
                    options={sortOptions}
                    value={sortBy}
                    onChange={handleSortChange}
                    orientation="row"
                    spacing={4}
                />

                <ButtonGroup>
                    {/*<Button
                        variant="subtle"
                        colorPalette="green"
                        onClick={() => handleExportReportType('reportsBestSellingExport', 'pdf')}
                        disabled={hasNoData}
                    >
                        <UI.Icon name="pdf"/>Exportar Pdf
                    </Button>*/}
                    <Button
                        variant="subtle"
                        colorPalette="blue"
                        onClick={handleExportCsv}
                        disabled={hasNoData}
                    >
                        <UI.Icon name="csv"/>Export CSV
                    </Button>
                </ButtonGroup>

                <Form.Interval
                    initial={initialPeriod}
                    onSelect={(p) => setPeriod(p)}
                />

                <UI.Show visible={!!validPeriod || sortBy}>
                    <Button variant="ghost" size="xs" onClick={handleResetFilters}>
                        <UI.Icon name="cancel"/> Limpar filtros
                    </Button>
                </UI.Show>
            </UI.Table>

            {hasNoData && (
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    p={8}
                    borderRadius="lg"
                    borderWidth="1px"
                    mt={4}
                >
                    <VStack spacing={4} textAlign="center">
                        <UI.Icon
                            name='empty'
                            size="48px"
                        />
                        <Text fontSize="lg" fontWeight="medium">
                            Nenhum produto encontrado
                        </Text>
                        <Text fontSize="sm">
                            Não há produtos vendidos no período selecionado
                        </Text>
                        <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleResetFilters}
                            mt={2}
                        >
                            <UI.Icon name="refresh"/>
                            Limpar filtros
                        </Button>
                    </VStack>
                </Box>
            )}
        </>
    )
}

const Row = ({data}) => {
    return (
        <Table.Row>
            <Table.Cell fontSize="xs">{data?.month}</Table.Cell>
            <Table.Cell>{data?.product}</Table.Cell>
            <Table.Cell textAlign="right" fontWeight="bold">
                {data?.orders} Pedidos
            </Table.Cell>
            <Table.Cell textAlign="right" fontWeight="bold">
                {data?.total} unidades
            </Table.Cell>
        </Table.Row>
    )
}